import { createFileRoute, Link } from "@tanstack/react-router";
import { useOrders } from "@/lib/orders";
import { formatPrice } from "@/lib/cart/pricing";
import { normaliseOrderLines, totalUnits } from "@/lib/orders/pack-lines";
import {
  AccountShell,
  SectionCard,
  EmptyState,
  GoldButton,
} from "@/components/account/AccountShell";

export const Route = createFileRoute("/account/orders")({
  component: OrdersPage,
});

function OrdersPage() {
  const { orders, loading } = useOrders();

  return (
    <AccountShell>
      <SectionCard title="Order History">
        {loading ? (
          <ul className="divide-y divide-black/10">
            {[0, 1, 2].map((i) => (
              <li key={i} className="flex animate-pulse items-center justify-between gap-4 py-5">
                <div className="space-y-2">
                  <div className="h-3 w-32 bg-black/10" />
                  <div className="h-3 w-48 bg-black/5" />
                </div>
                <div className="h-3 w-16 bg-black/10" />
              </li>
            ))}
          </ul>
        ) : orders.length === 0 ? (
          <EmptyState
            title="No orders yet"
            description="Once you place an order, it will appear here with tracking and details."
            action={
              <Link to="/fragrances" search={{}}>
                <GoldButton>Discover the collection</GoldButton>
              </Link>
            }
          />
        ) : (
          <ul className="divide-y divide-black/10">
            {orders.map((o) => {
              const lines = normaliseOrderLines(o.lines);
              const units = totalUnits(lines);
              return (
              <li key={o.id} className="grid gap-4 py-6 sm:grid-cols-[1fr_auto] sm:items-start">
                <div>
                  <div className="flex flex-wrap items-baseline gap-3">
                    <p className="text-sm font-medium text-black">{o.id}</p>
                    <p className="text-xs text-black/50">
                      {new Date(o.createdAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-black/60">
                    {lines.length} line{lines.length !== 1 ? "s" : ""} · {units} bottle
                    {units !== 1 ? "s" : ""} · Ship to {o.shipping.city}, {o.shipping.country}
                  </p>
                  <ul className="mt-2 space-y-1.5 text-sm text-black/70">
                    {lines.map((l) => (
                      <li key={l.key}>
                        <span className="text-black">{l.name}</span>
                        {l.pack ? (
                          <span className="text-black/50">
                            {" "}
                            · {l.pack.bottleCount * l.quantity} bottles ·{" "}
                            {l.pack.bottles
                              .map((b) => `${b.name} ×${b.qty * l.quantity}`)
                              .join(", ")}
                          </span>
                        ) : (
                          <span className="text-black/50">
                            {" "}
                            · {l.variantLabel} × {l.quantity}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex items-center gap-6 sm:flex-col sm:items-end">
                  <p className="text-sm text-black">{formatPrice(o.pricing.total)}</p>
                  <Link
                    to="/order/$id"
                    params={{ id: o.id }}
                    className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#C6A15B] hover:underline"
                  >
                    View details →
                  </Link>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
    </AccountShell>
  );
}
